import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import { navigate } from '../../actions';
import {
    Button,
    Dimensions,
    Image,
    Img,
    List,
    Platform,
    Section,
    Subtitle,
    Title,
    View,
    withState
} from '../../common';
import {
    Log,
    QAMode
} from '../../common/Log';
import { useBackup } from '../../hooks/useBackup';
import { getDocuments } from '../../selectors';
import { tryShowImageFromCache } from '../../services/device';
import {
    checkUrls,
    checkUrlsAndroid
} from '../Visits/VisitImagesSlider';

export const IOS_TEMP_PATH = '~/tmp';
export const IOS_DOCS_PATH = '~/Documents';

const imageSize = Dimensions.get('window').width / 2 - 20;

const EmptyDocumentsListStub = () => {
    const {
        iCloudEnabled,
        googleCloudEnabled,
        backupMyDocs,
        useGoogleBackup,
    } = useBackup({ useInteractive: false });

    const showWarning = useMemo(() => {
        let cloudEnabled = false;
        if (Platform.OS === 'android') {
            cloudEnabled = googleCloudEnabled;
        } else if (Platform.OS === 'ios') {
            cloudEnabled = iCloudEnabled || (googleCloudEnabled && useGoogleBackup);
        } else {
            return false;
        }

        return !cloudEnabled || !backupMyDocs;
    }, [ backupMyDocs,
        googleCloudEnabled,
        iCloudEnabled,
        useGoogleBackup ]);

    return <View
        style={{
            flex: 1,
            alignItems: 'center',
            paddingHorizontal: 16,
            marginTop: 64,
        }}
    >
        <Img.EmptyDocs style={{ marginBottom: 36 }}/>
        <Title
            id="titles.noAttachedDocs"
            numberOfLines={null}
            bold
            centered
        />
        {showWarning && <Subtitle
            id="titles.noAttachedDocsHintBackup"
            style={{
                marginTop: 16,
                color: '#AAAAAA',
            }}
            numberOfLines={null}
            centered
        />}
    </View>;
};

const DocumentsListItem = withState(
    ({
        item: {
            uri,
            path,
            origURL,
            // cachePath,
            id,
        },
        item,
        onPress,
        setState,
        errorOccurred,
        retryRender,
    }) => {
        const [ url, setUrl ] = useState({
            uri: path,
            // resolveImagePath(uri, Platform.OS === 'ios' ? path || cachePath : cachePath || path)
        });
        return (
            <Button
                action={onPress}
                styles={{
                    alignSelf: 'center',
                    marginVertical: 6,
                }}
            >
                <Image
                    key={`${id}-${retryRender ? 1 : 0}`}
                    accessibilityLabel="docs-list_image"
                    resizeMode="cover"
                    style={[
                        {
                            alignSelf: 'center',
                            width: imageSize,
                            height: imageSize,
                        },
                        errorOccurred ? { backgroundColor: 'gray' } : {},
                    ]}
                    source={url}
                    onError={() => {
                        if (Platform.OS === 'ios') {
                            const uri = url?.uri ?? '';
                            const fileName = uri?.split('/')
                                ?.pop
                                ?.() ?? '';
                            let newUri = { uri };
                            if (uri?.startsWith('~/tmp')) {
                                newUri = { uri: uri.replace('~/tmp', '~/Documents') };
                            } else if (uri?.startsWith('~/Documents')) {
                                newUri = { uri: uri.replace('~/Documents', '~/tmp') };
                            } else if (uri.includes('tmp')) {
                                newUri = { uri: `~/tmp/${fileName}` };
                            } else if (uri.includes('documents')) {
                                newUri = { uri: `~/Documents/${fileName}` };
                            }
                            setUrl(newUri);
                        }
                        if (Platform.OS === 'ios' && origURL) {
                            // checkPhotosLibrary()
                            //     .then(async (granted) => {
                            //         if (granted) {
                            //             try {
                            //                 await copyImageToDocuments(origURL, uri);
                            //                 setState({ retryRender: !retryRender });
                            //             } catch (err) {
                            //                 setState({ errorOccurred: true });
                            //             }
                            //         } else {
                            //             setState({ errorOccurred: true });
                            //         }
                            //     });
                        } else {
                            tryShowImageFromCache(
                                path,
                                item,
                                uri,
                                () => setState({ retryRender: !retryRender }),
                                () => setState({ errorOccurred: true }),
                            );
                        }
                    }}
                />
            </Button>
        );
    },
);

export const DocumentsPhoto = ({
    isFetching,
    isLoading,
}) => {
    const [ documents, setDocuments ] = useState([]);
    const [ isUpdating, setIsUpdating ] = useState(true);
    const storedDocs = useSelector(getDocuments);

    useEffect(() => {
        const fn = Platform.OS === 'ios' ? checkUrls : checkUrlsAndroid;
        fn(storedDocs)
            .then((urls) => {
                setDocuments(urls);
            })
            .catch(e => console.log('Error check urls', e))
            .finally(() => setTimeout(() => setIsUpdating(false), 0));

    }, [ storedDocs ]);

    const {
        googleCloudEnabled,
        iCloudEnabled,
        backupMyDocs,
    } = useBackup({ useInteractive: false });

    const getHint = useCallback(() => {
        if (documents.length) {
            if (!backupMyDocs || (Platform.OS === 'ios' && (!iCloudEnabled && !googleCloudEnabled)) || (Platform.OS === 'android' && !googleCloudEnabled)) {
                return 'titles.localFileAddedMakeBackup';
            }
        }
        return null;
    }, [ documents,
        backupMyDocs,
        iCloudEnabled,
        googleCloudEnabled ]);

    return (
        <Section
            style={{ flex: 1 }}
            title="titles.myDocuments"
            capitalize
            isLoading={isFetching || isLoading}
        >
            {!isUpdating && <List
                data={documents}
                numColumns={2}
                columnWrapperStyle={{ justifyContent: 'space-between' }}
                ListEmptyComponent={isFetching ? <View/> : <EmptyDocumentsListStub/>}
                renderItem={({ item }) => (
                    <DocumentsListItem
                        item={item}
                        onPress={() =>
                            navigate('DocumentsSlider', {
                                initialId: item.id,
                                enableDelete: true,
                            })
                        }
                    />
                )}
            />}
            <QAMode><Log value={documents}/></QAMode>
        </Section>
    );
};
